import emoji from 'node-emoji';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { Alert, StyleSheet, View, } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useScrollToTop } from '@react-navigation/native';
import Animated, { runOnJS, runOnUI, scrollTo, useAnimatedRef, useAnimatedScrollHandler, useDerivedValue, useSharedValue, } from 'react-native-reanimated';
import { setTransactionsRequesting } from 'actions';
import { AppView, rem } from 'design-system';
import Modal from 'utils/packages/Modal';
import StatusBar from 'design-system/StatusBar';
import { isPremiumTier, periodMap as periodRankMap, Tier, tierGreaterThan, tierLevelMap, TierPeriod, } from 'features/premium/entities';
import { isUnlocked } from 'features/premium/isUnlocked';
import { selectDidViewFraudDetectionIntro } from 'features/quests/store/selectors';
import { selectHasCurrentRentReporting } from 'features/rent-reporting/selectors';
import { useFocusEffect, useTierLimits } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { indexOf, noop } from 'lodash';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import Amplitude from 'utils/amplitude';
import { formatAmount, formatDateForItem } from 'utils';
import { triggerSelectorPress } from 'utils/hapticFeedback';
import useIsTablet from 'hooks/useIsTablet';
import { useSmartRulesNavigation } from 'hooks/useSmartRulesNavigation';
import { useTabNavigation } from 'hooks/navigation/useTabNavigation';
import usePlaidLink from 'hooks/usePlaidLink';
import { showExportDataMethodModal } from 'features/export-data/ExportDataMethodModal';
import { selectHasLiveExportFeature } from 'features/export-data-live/selectors';
import useConnectionsLimitModal from 'features/link-bank/hooks/useConnectionsLimitModal';
import { useNavigateToAutosave } from 'features/pots/hooks/useNavigateToAutosave';
import { selectIsGBUser } from 'reducers/selectors';
import { isAndroid } from '../../constants';
import { tierThemes } from './benefits';
import CompareButton from './components/CompareButton';
import PremiumScreenHeader from './components/PremiumScreenHeader';
import PremiumTierView from './components/PremiumTierView';
import { getTierSubscriptions } from './hooks';
import useCanChangeSubscriptionPeriod from './hooks/useCanChangeSubscriptionPeriod';
import useStripePayment from './hooks/useStripePayment';
import { selectActiveTier, selectAvailableIAPSubscriptionsWithPrices, selectDefaultPaywallTier, selectEmmaProStatusSKU, selectIsOnReverseTrial, selectShouldShowStripe, selectStripePriceMap, selectSubscriptionPrices, } from './selectors';
import { useActivatePremiumTrialModal } from './trial/hooks/useActivatePremiumTrialModal';
import { selectUserTier } from './selectors/tier';
const AnimatedScrollView = Animated.createAnimatedComponent(ScrollView);
const descendingTierValueSort = (t1, t2) => {
    const l1 = tierLevelMap[t1];
    const l2 = tierLevelMap[t2];
    if (l1 < l2) {
        return 1;
    }
    if (l1 > l2) {
        return -1;
    }
    return 0;
};
const ascendingTierValueSort = (t1, t2) => {
    const l1 = tierLevelMap[t1];
    const l2 = tierLevelMap[t2];
    if (l1 < l2) {
        return -1;
    }
    if (l1 > l2) {
        return 1;
    }
    return 0;
};
const PremiumScreen = ({ navigation, isInTab, onUpgradeComplete, onContinueOnFree, tierScreen, promoProductId, isTierRequired, subscriptions, showDowngradePeriodButton, showBackButton, showFreeTier, // This is only used for a test in onboarding. DONOT use this else where
title, analyticsFlow, benefitId, currentAndAbovePlan, reverseOrder, showNewViewAllPlansScreen, }) => {
    const isGBUser = useAppSelector(selectIsGBUser);
    const dispatch = useAppDispatch();
    const { navigateToTab } = useTabNavigation();
    const navigateToAutosave = useNavigateToAutosave();
    const { canUseStripe } = useStripePayment();
    const { width: WIDTH, paddingHorizontal } = useAppFrameDimensions(isInTab);
    const screenWidth = WIDTH + (paddingHorizontal || 0) * 2;
    const horizontalScrollViewRef = useAnimatedRef();
    const tierFreeRef = useAnimatedRef();
    const tierProRef = useAnimatedRef();
    const tierPlusRef = useAnimatedRef();
    const tierUltimateRef = useAnimatedRef();
    const activeTier = useAppSelector(selectActiveTier);
    const userTier = useAppSelector(selectUserTier);
    const autoRenewProductId = useAppSelector((store) => store.user.user.emmaProStatus.autoRenewProductId);
    const isOnReverseTrial = useAppSelector(selectIsOnReverseTrial);
    const defaultPaywallTier = useAppSelector(selectDefaultPaywallTier);
    const tiersInOrder = useMemo(() => {
        if (showBackButton) {
            return [tierScreen || defaultPaywallTier];
        }
        const shouldShowFreeTier = showFreeTier ||
            (isGBUser && !isPremiumTier(activeTier) && showFreeTier === undefined);
        const tiers = Object.values(Tier)
            .filter((t) => {
            if (currentAndAbovePlan) {
                // Show current tier and all tiers above
                return tierGreaterThan(t, userTier) || t === userTier;
            }
            if (t === Tier.free) {
                return shouldShowFreeTier && !isTierRequired;
            }
            if (showDowngradePeriodButton) {
                // User is in settings and can change tier
                return true;
            }
            if (!isTierRequired ||
                tierScreen === t ||
                tierScreen === undefined ||
                tierGreaterThan(t, tierScreen)) {
                if (t === activeTier) {
                    // Only show the active tier if they are in the "Premium tab" (US users)
                    return isInTab || isOnReverseTrial;
                }
                // Show any tiers above the users current tier (or all if they’re on a reverse trial)
                return !isUnlocked(userTier, t) || isOnReverseTrial;
            }
            return false;
        })
            .sort(reverseOrder ? ascendingTierValueSort : descendingTierValueSort);
        return tiers;
    }, [
        showBackButton,
        showFreeTier,
        defaultPaywallTier,
        isGBUser,
        activeTier,
        tierScreen,
        showDowngradePeriodButton,
        currentAndAbovePlan,
        isTierRequired,
        userTier,
        isOnReverseTrial,
        isInTab,
        reverseOrder,
    ]);
    const showSelector = useMemo(() => {
        if (currentAndAbovePlan) {
            return tiersInOrder.length > 1;
        }
        return true;
    }, [currentAndAbovePlan, tiersInOrder.length]);
    const scrollToTier = useCallback((tier, animated = true) => {
        const index = indexOf(tiersInOrder, tier);
        if (index !== -1) {
            // eslint-disable-next-line no-undef
            requestAnimationFrame(() => {
                horizontalScrollViewRef.current?.scrollTo({
                    x: index * screenWidth,
                    animated,
                });
            });
        }
    }, [horizontalScrollViewRef, screenWidth, tiersInOrder]);
    const prevActiveTier = useRef(activeTier);
    /**
     * delay rendering of notSelectedList START
     */
    const [renderNotSelectedTiers, setRenderNotSelectedTiers] = useState(false);
    const initialSelectedTier = useRef((() => {
        if (currentAndAbovePlan && userTier === Tier.free) {
            scrollToTier(Tier.free);
            return Tier.free;
        }
        if (promoProductId && subscriptions.length) {
            const selected = subscriptions.find((it) => it.productId === promoProductId);
            if (selected) {
                scrollToTier(selected.tier);
            }
        }
        if (tierScreen) {
            return tierScreen;
        }
        if (isPremiumTier(activeTier) &&
            (showDowngradePeriodButton ||
                currentAndAbovePlan ||
                isInTab ||
                isOnReverseTrial)) {
            return activeTier;
        }
        return tiersInOrder[0];
    })());
    useEffect(() => {
        const timeout = setTimeout(() => {
            setRenderNotSelectedTiers(true);
        }, 500);
        return () => {
            clearTimeout(timeout);
        };
    }, []);
    /**
     * delay rendering of notSelectedList END
     */
    useEffect(() => {
        if (activeTier && (!tierScreen || prevActiveTier.current !== activeTier)) {
            scrollToTier(activeTier, false);
        }
        prevActiveTier.current = activeTier;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTier, scrollToTier]);
    const scrolledToTierScreen = useRef(false);
    useEffect(() => {
        if (scrolledToTierScreen.current) {
            return;
        }
        if (horizontalScrollViewRef.current &&
            screenWidth &&
            tierScreen &&
            tiersInOrder?.includes(tierScreen)) {
            scrollToTier(tierScreen, false);
            scrolledToTierScreen.current = true;
        }
    }, [
        tiersInOrder,
        tierScreen,
        horizontalScrollViewRef,
        screenWidth,
        scrollToTier,
    ]);
    useEffect(() => {
        if (promoProductId && subscriptions.length) {
            const selected = subscriptions.find((it) => it.productId === promoProductId);
            if (selected?.tier) {
                scrollToTier(selected.tier);
            }
        }
    }, [subscriptions, promoProductId, scrollToTier]);
    const tMap = useMemo(() => ({
        [Tier.free]: tierFreeRef,
        [Tier.pro]: tierProRef,
        [Tier.plus]: tierPlusRef,
        [Tier.ultimate]: tierUltimateRef,
    }), [tierFreeRef, tierProRef, tierPlusRef, tierUltimateRef]);
    const scrollX = useSharedValue(0);
    const currentIndex = useDerivedValue(() => Math.round(Math.abs(scrollX.value / screenWidth)), [screenWidth, scrollX]);
    const currentTierDerivedValue = useDerivedValue(() => tiersInOrder[currentIndex.value], [currentIndex, tiersInOrder]);
    const scrollY = useSharedValue(0);
    // On the New Architecture, scrollTo emits the target list's onScroll
    // synchronously, which re-enters this handler. Ignore those nested events,
    // otherwise the tier lists can keep scrolling each other until the UI
    // thread's stack overflows.
    const isSyncingTierScroll = useSharedValue(false);
    const onScrollIOS = useAnimatedScrollHandler({
        onScroll: (nativeEvent) => {
            if (isSyncingTierScroll.value)
                return;
            scrollY.value = nativeEvent.contentOffset.y;
            isSyncingTierScroll.value = true;
            try {
                tiersInOrder
                    .filter((tier) => tier !== currentTierDerivedValue.value)
                    .forEach((tier) => {
                    const tierRef = tMap[tier];
                    if (!tierRef || tierRef() == null)
                        return;
                    scrollTo(tierRef, 0, nativeEvent.contentOffset.y, false);
                });
            }
            finally {
                isSyncingTierScroll.value = false;
            }
        },
    }, [currentTierDerivedValue, tiersInOrder, tMap]);
    // On Android, scrollTo is applied on a later frame, so the scroll events it
    // causes arrive after isSyncingTierScroll is reset. Syncing on every frame
    // made the tier lists keep scrolling each other, and scrolling slowed down
    // until it almost stopped. Only one tier is on screen while scrolling
    // vertically, so on Android the hidden tiers are synced once the scroll comes
    // to rest, and again before a horizontal swipe or a tier tap shows another.
    const syncHiddenTierScroll = useCallback((y) => {
        'worklet';
        tiersInOrder
            .filter((tier) => tier !== currentTierDerivedValue.value)
            .forEach((tier) => {
            const tierRef = tMap[tier];
            if (!tierRef || tierRef() == null)
                return;
            scrollTo(tierRef, 0, y, false);
        });
    }, [currentTierDerivedValue, tiersInOrder, tMap]);
    // True while the user drags or flings the shown tier. Scroll events outside
    // that come from the hidden tiers being synced, which may stop short of the
    // shown tier's offset on a shorter list, so they don't move the header.
    const isScrollingShownTier = useSharedValue(false);
    const onScrollAndroid = useAnimatedScrollHandler({
        onBeginDrag: () => {
            isScrollingShownTier.value = true;
        },
        onScroll: (nativeEvent) => {
            if (!isScrollingShownTier.value)
                return;
            scrollY.value = nativeEvent.contentOffset.y;
        },
        onEndDrag: (nativeEvent) => {
            // A fling continues in momentum events; sync when it ends instead.
            if (Math.abs(nativeEvent.velocity?.y ?? 0) > 0.1)
                return;
            isScrollingShownTier.value = false;
            syncHiddenTierScroll(nativeEvent.contentOffset.y);
        },
        onMomentumBegin: () => {
            isScrollingShownTier.value = true;
        },
        onMomentumEnd: (nativeEvent) => {
            isScrollingShownTier.value = false;
            syncHiddenTierScroll(nativeEvent.contentOffset.y);
        },
    }, [syncHiddenTierScroll]);
    const onScroll = isAndroid ? onScrollAndroid : onScrollIOS;
    const store = useAppStore();
    const hasLiveExportFeature = useAppSelector(selectHasLiveExportFeature);
    const { periodMap, isFetchingPeriodMap, onChangedSubscriptionPeriod } = useCanChangeSubscriptionPeriod(subscriptions, tiersInOrder);
    const prices = useAppSelector(selectSubscriptionPrices);
    const doOnPurchase = useCallback((productId, subscribedToProduct, discount, proratedRefund) => {
        onUpgradeComplete?.(subscribedToProduct?.id || productId, subscribedToProduct, subscribingCurrentlyActiveProductId.current, discount, proratedRefund);
        if (isChangingSubscriptionPeriod.current) {
            onChangedSubscriptionPeriod(subscribedToProduct?.id || productId);
            isChangingSubscriptionPeriod.current = false;
        }
    }, [onUpgradeComplete, onChangedSubscriptionPeriod]);
    useFocusEffect(useCallback(() => {
        const entry = StatusBar.pushStackEntry({
            barStyle: 'dark-content',
            animated: true,
        });
        return () => {
            StatusBar.popStackEntry(entry);
        };
    }, []));
    const emmaProStatusSKU = useAppSelector(selectEmmaProStatusSKU);
    const emmaProStatus = useAppSelector((state) => state.user.user.emmaProStatus);
    const subscribingCurrentlyActiveProductId = useRef(emmaProStatusSKU);
    const isChangingSubscriptionPeriod = useRef(false);
    const { openUpgradeModalWithSubscriptions, isFetching, isFetchingStripePreview: isFetchingPreview, } = useActivatePremiumTrialModal({
        onUpgradeComplete: doOnPurchase,
        analyticsFlow,
        benefitId,
    });
    const openSubscribePopup = useCallback((tier) => {
        const subscriptions = selectAvailableIAPSubscriptionsWithPrices(store.getState());
        const stripePriceMap = selectStripePriceMap(store.getState());
        const canUseStripe = selectShouldShowStripe(store.getState());
        subscribingCurrentlyActiveProductId.current = emmaProStatusSKU;
        const currentSubscriptions = getTierSubscriptions(subscriptions, tier, canUseStripe, stripePriceMap);
        const period = (() => {
            if (emmaProStatus.tier !== tier)
                return undefined;
            if (periodMap[tier])
                return periodMap[tier];
            if (emmaProStatus.period === 'yearly')
                return TierPeriod.yearly;
            if (emmaProStatus.autoRenewTier && emmaProStatus.autoRenewTier === tier)
                return TierPeriod.monthly;
            return TierPeriod.yearly;
        })();
        const newSubscriptionPeriod = period
            ? currentSubscriptions.find((sub) => sub.period === period)
            : undefined;
        if (newSubscriptionPeriod && !isOnReverseTrial) {
            // User is changing their subscription period (Monthly -> Yearly OR Yearly -> Monthly of the same tier)
            if (newSubscriptionPeriod) {
                isChangingSubscriptionPeriod.current = true;
                openUpgradeModalWithSubscriptions([newSubscriptionPeriod], () => {
                    openSubscribePopup(tier);
                }, false);
            }
        }
        else {
            openUpgradeModalWithSubscriptions(currentSubscriptions, () => {
                openSubscribePopup(tier);
            }, false);
        }
    }, [
        emmaProStatus.autoRenewTier,
        emmaProStatus.period,
        emmaProStatus.tier,
        emmaProStatusSKU,
        isOnReverseTrial,
        openUpgradeModalWithSubscriptions,
        periodMap,
    ]);
    // Changing plan abandons the active subscription's scheduled renewal, taking
    // any current/renewal discount with it. If the user is heading to a lower
    // tier or a shorter period while a discount is in play, warn them first.
    const onSubscribePress = useCallback((tier) => {
        const currentTier = emmaProStatusSKU?.tier;
        const currentPeriod = emmaProStatusSKU?.period;
        const targetPeriod = periodMap[tier];
        const isTierDowngrade = !!currentTier && tierLevelMap[tier] < tierLevelMap[currentTier];
        const isPeriodDowngrade = tier === currentTier &&
            !!targetPeriod &&
            !!currentPeriod &&
            periodRankMap[targetPeriod] < periodRankMap[currentPeriod];
        const discount = emmaProStatus.autoRenewPrice?.discount;
        if ((isTierDowngrade || isPeriodDowngrade) && discount) {
            const tierName = tierThemes[emmaProStatus.autoRenewTier ?? emmaProStatus.tier ?? tier]
                .name;
            const renewsOn = emmaProStatus.expiresOn
                ? ` on ${formatDateForItem(emmaProStatus.expiresOn)}`
                : '';
            const discountMessage = discount.percentOff
                ? `${discount.percentOff}%`
                : `${formatAmount(discount.amountOff, emmaProStatus.currentPrice?.currency)}`;
            const isFreeNextMonth = emmaProStatus.autoRenewPeriod === 'monthly' &&
                discount.percentOff === 100;
            Modal.showConfirmation({
                title: isFreeNextMonth
                    ? `Don’t lose your free month`
                    : `Don’t lose your ${discountMessage} discount`,
                subtitle: isFreeNextMonth
                    ? `You have a month of ${tierName} for ${formatAmount(0, emmaProStatus.currentPrice?.currency, 2)} waiting for you${renewsOn}. If you change or cancel your plan now, you’ll lose this offer.`
                    : `You have a ${discountMessage} discount waiting for your ${tierName} renewal${renewsOn}. If you change your plan, you’ll lose this offer.`,
                cancelButtonTitle: isFreeNextMonth
                    ? 'Keep my free month'
                    : 'Keep my discount',
                confirmButtonTitle: 'Still change plan',
                onConfirm: () => openSubscribePopup(tier),
            });
            return;
        }
        openSubscribePopup(tier);
    }, [
        emmaProStatus.autoRenewPeriod,
        emmaProStatus.autoRenewPrice?.discount,
        emmaProStatus.autoRenewTier,
        emmaProStatus.currentPrice?.currency,
        emmaProStatus.expiresOn,
        emmaProStatus.tier,
        emmaProStatusSKU?.period,
        emmaProStatusSKU?.tier,
        openSubscribePopup,
        periodMap,
    ]);
    const openPlaid = usePlaidLink();
    const connectionsLimitModal = useConnectionsLimitModal();
    const { onPressSmartRules } = useSmartRulesNavigation();
    const onPressBenefit = useCallback((benefit) => {
        if (!navigation)
            return;
        switch (benefit.id) {
            case 'connections':
                navigation.navigateDeprecated('Logins');
                break;
            case 'cashback':
                navigation.navigateDeprecated('Rewards');
                break;
            case 'turboUpdates':
                break;
            case 'prioritySupport':
                navigation.navigateDeprecated('Support', {});
                Amplitude.logEvent('Premium.LiveChat');
                break;
            case 'dataProtection':
                if (selectDidViewFraudDetectionIntro(store.getState())) {
                    navigation.navigateDeprecated('DataProtectionScreen');
                }
                else {
                    navigation.navigateDeprecated('FraudProtectionIntro');
                }
                break;
            case 'trueBalance':
                Alert.alert('True Balance', 'You can now see your real balance in your main accounts, just set payday to payday budgeting.');
                break;
            case 'billReminders':
                Alert.alert(emoji.emojify('Bill Reminders'), 'You can enable bill reminder notifications from the recurring payments screen.');
                break;
            case 'netWorth':
                navigation.navigateDeprecated('NetWorth');
                break;
            case 'nordVPN':
                navigation.navigateDeprecated('ManageNordVpn');
                break;
            case 'transfers':
                navigation.navigateDeprecated('Transfer');
                break;
            case 'savingGoals':
                Alert.alert('Saving goals', 'You can now create and see your saving goals in your pots, savings and investment accounts.');
                break;
            case 'automatedSavings':
                navigateToAutosave();
                break;
            case 'autoInvest':
                navigateToTab('Invest');
                break;
            case 'customCategories':
                navigation.navigateDeprecated('ManageCategories');
                break;
            case 'offlineAccounts':
                navigation.navigateDeprecated('OfflineAccounts');
                break;
            case 'rollingBudgets':
                Alert.alert(emoji.emojify('Rolling Budgets'), 'Tap edit in Budgeting to change your budgets to rolling.');
                break;
            case 'export':
                if (hasLiveExportFeature) {
                    showExportDataMethodModal();
                }
                else {
                    navigation.navigateDeprecated('ExportData');
                }
                break;
            case 'smartRules':
                onPressSmartRules(false);
                break;
            case 'spaces':
                navigation.navigateDeprecated('MySpaces');
                break;
            case 'advancedTransactionsEditing':
                dispatch(setTransactionsRequesting);
                navigation.navigateDeprecated('TransactionsList');
                break;
            case 'business':
                if (isGBUser) {
                    navigation.navigateDeprecated('ChooseBank', {
                        back: true,
                        business: true,
                    });
                }
                else {
                    connectionsLimitModal(openPlaid);
                }
                break;
            case 'split':
                Alert.alert(emoji.emojify('Split Transactions :scissors:'), 'We have added a menu on the top right corner of each transaction view. You can split your transactions from there!');
                break;
            case 'renameTransactions':
                Alert.alert(emoji.emojify('Rename Transactions :pencil2:'), 'We have added a menu on the top right corner of each transaction view. You can rename your transactions from there!');
                break;
            case 'extraMembers':
                navigation.navigateDeprecated('ExtraMembersIntro');
                break;
            case 'changeTransactionDate':
                Alert.alert('Change of date', 'You can now change your transactions’ dates to make them more accurate');
                break;
            case 'merchantBudgets':
                navigation.navigateDeprecated('EditMerchantBudgets');
                break;
            case 'rentReporting':
                if (selectHasCurrentRentReporting(store.getState())) {
                    navigation.navigateDeprecated('RentReporting');
                }
                else {
                    navigation.navigateDeprecated('RentReportingOnboarding');
                }
                break;
            default:
                break;
        }
    }, [
        connectionsLimitModal,
        hasLiveExportFeature,
        isGBUser,
        navigateToAutosave,
        navigateToTab,
        onPressSmartRules,
        openPlaid,
    ]);
    const animatedScrollHandler = useAnimatedScrollHandler({
        onBeginDrag: () => {
            if (isAndroid)
                syncHiddenTierScroll(scrollY.value);
        },
        onEndDrag: (nativeEvents) => {
            const xOffset = nativeEvents.contentOffset.x;
            if (xOffset < 0 || xOffset > (tiersInOrder.length - 1) * screenWidth) {
                return;
            }
            runOnJS(triggerSelectorPress)();
        },
        onScroll: (nativeEvent) => {
            scrollX.value = nativeEvent.contentOffset.x;
        },
    }, [tiersInOrder, syncHiddenTierScroll]);
    const onSelectView = useCallback((index) => {
        triggerSelectorPress();
        if (isAndroid)
            runOnUI(syncHiddenTierScroll)(scrollY.value);
        horizontalScrollViewRef.current?.scrollTo({
            x: index * screenWidth,
            animated: true,
        });
    }, [horizontalScrollViewRef, screenWidth, scrollY, syncHiddenTierScroll]);
    useTierLimits();
    useScrollToTop(useRef({
        scrollToTop: () => {
            tMap[currentTierDerivedValue.value].current?.scrollToOffset({
                offset: 0,
            });
        },
    }));
    const isTablet = useIsTablet();
    const rightContent = useMemo(() => isInTab ? (<View style={isTablet ? styles.compareBtn : null}>
          <CompareButton isInTab alwaysShow/>
        </View>) : undefined, [isInTab, isTablet]);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <PremiumScreenHeader scrollY={scrollY} scrollX={scrollX} currentTierDerivedValue={currentTierDerivedValue} screenWidth={screenWidth} activeTier={userTier} onSelectView={onSelectView} tiersInOrder={tiersInOrder} rightContent={rightContent} title={showDowngradePeriodButton ? 'Manage plan' : title || 'Upgrade'} isInTab={isInTab} showSelector={showSelector}/>
      <AnimatedScrollView horizontal showsHorizontalScrollIndicator={false} snapToInterval={screenWidth} onScroll={animatedScrollHandler} decelerationRate="fast" scrollEventThrottle={16} scrollEnabled keyboardShouldPersistTaps="handled" ref={horizontalScrollViewRef} 
    // Android will not send onMomentumEnd events by default, we must pass noop as the animatedScrollHandler requires them
    // https://github.com/software-mansion/react-native-reanimated/issues/2735
    // https://github.com/facebook/react-native/blob/ef6ab3f5cad968d7b2c9127d834429b0f4e1b2cf/Libraries/Components/ScrollView/ScrollView.js#L1741-L1744
    onMomentumScrollEnd={noop}>
        {tiersInOrder.map((tier) => (<PremiumTierView key={tier} tMap={tMap} tier={tier} prices={prices} isInTab={isInTab} onScroll={onScroll} periodMap={periodMap} activeTier={activeTier} screenWidth={screenWidth} canUseStripe={canUseStripe} isFetchingPro={isFetching || isFetchingPreview} onPressBenefit={onPressBenefit} showBackButton={showBackButton} isOnReverseTrial={isOnReverseTrial} onContinueOnFree={onContinueOnFree} openSubscribePopup={onSubscribePress} autoRenewProductId={autoRenewProductId} isFetchingPeriodMap={isFetchingPeriodMap} showNewViewAllPlansScreen={showNewViewAllPlansScreen} showDowngradePeriodButton={showDowngradePeriodButton} renderTier={renderNotSelectedTiers || initialSelectedTier.current === tier} showSelector={showSelector}/>))}
      </AnimatedScrollView>
    </AppView>);
};
const styles = StyleSheet.create({
    compareBtn: {
        marginRight: rem(16),
        alignItems: 'flex-end',
    },
});
export default React.memo(PremiumScreen);
